import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  ACTIVE_JOB_STATUSES,
  JobRunStatus,
  JobRunView,
  useCancelJobMutation,
  useListJobsQuery,
} from "~/ui-rtk/api/jobsApi"
import { describeJob, getJobLabel } from "./jobViews"

const STATUS_BADGE: Record<JobRunStatus, { label: string; cls: string }> = {
  AWAITING_APPROVAL: {
    label: "Awaiting approval",
    cls: "bg-amber-500/20 text-amber-300",
  },
  SCHEDULED: { label: "Scheduled", cls: "bg-sky-500/20 text-sky-300" },
  PENDING: { label: "Queued", cls: "bg-sky-500/20 text-sky-300" },
  PROCESSING: { label: "In progress", cls: "bg-amber-500/20 text-amber-300" },
  COMPLETED: { label: "Completed", cls: "bg-emerald-500/20 text-emerald-300" },
  FAILED: { label: "Failed", cls: "bg-rose-500/20 text-rose-300" },
  REJECTED: { label: "Rejected", cls: "bg-rose-500/20 text-rose-300" },
  CANCELLED: {
    label: "Cancelled",
    cls: "bg-system-gray-500/20 text-system-gray-300",
  },
}

function relativeTime(iso?: string | null): string {
  if (!iso) return ""
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (secs < 60) return "just now"
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

/** Absolute short local datetime, e.g. "Jul 10, 9:00 AM". */
function formatDateTime(iso?: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function ResultCell({ job }: { job: JobRunView }) {
  const summary = job.result?.summary
  if (summary) {
    return (
      <span>
        <span className="text-emerald-400">{summary.successful} ok</span>
        {summary.failed > 0 && (
          <span className="text-rose-400"> · {summary.failed} failed</span>
        )}
        <span className="text-system-gray-500"> · {summary.total} total</span>
      </span>
    )
  }
  // SCHEDULED is an "active" status but isn't running yet — surface when it will
  // fire rather than the generic "Working…" step label.
  if (job.status === "SCHEDULED") {
    return (
      <span className="text-sky-300">
        {job.scheduledFor
          ? `Scheduled for ${formatDateTime(job.scheduledFor)}`
          : "Scheduled"}
      </span>
    )
  }
  if (ACTIVE_JOB_STATUSES.includes(job.status)) {
    return <span className="text-amber-300">{job.step || "Working…"}</span>
  }
  if (job.status === "FAILED") {
    return (
      <span className="truncate text-rose-300">{job.error ?? "Failed"}</span>
    )
  }
  return <span className="text-system-gray-500">—</span>
}

function JobRow({
  job,
  onOpen,
  onCancel,
  isCancelling,
}: {
  job: JobRunView
  onOpen: (id: string) => void
  onCancel: (id: string) => void
  isCancelling: boolean
}) {
  const badge = STATUS_BADGE[job.status]
  const subtitle = describeJob(job)
  const cancellable = ACTIVE_JOB_STATUSES.includes(job.status)

  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={() => onOpen(job.id)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") onOpen(job.id)
      }}
      className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-white/5"
    >
      <td className="py-3 pr-3 align-top">
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}
        >
          {badge.label}
        </span>
      </td>
      <td className="py-3 pr-3 align-top">
        <div className="text-sm font-medium text-system-gray-100">
          {getJobLabel(job.type)}
        </div>
        {subtitle && (
          <div className="font-mono text-[11px] text-system-gray-500">
            {subtitle}
          </div>
        )}
      </td>
      <td className="py-3 pr-3 align-top text-sm text-system-gray-300">
        <ResultCell job={job} />
      </td>
      <td className="py-3 pr-3 align-top text-right text-[11px] text-system-gray-500">
        {relativeTime(job.completedAt ?? job.enqueuedAt)}
      </td>
      <td className="whitespace-nowrap py-3 pr-3 align-top text-right text-[11px] text-system-gray-500">
        {formatDateTime(job.completedAt ?? job.enqueuedAt)}
      </td>
      <td className="py-3 align-top text-right">
        {cancellable && (
          <button
            type="button"
            disabled={isCancelling}
            onClick={e => {
              e.stopPropagation()
              onCancel(job.id)
            }}
            className="btn btn-ghost btn-xs text-rose-300 hover:bg-rose-500/10"
          >
            {isCancelling ? "Cancelling…" : "Cancel"}
          </button>
        )}
      </td>
    </tr>
  )
}

export default function JobsPage() {
  const [, setSearchParams] = useSearchParams()

  const {
    data: jobs,
    isLoading,
    isError,
    refetch,
  } = useListJobsQuery(
    { take: 100 },
    // Poll while anything is running; RTK re-reads this each render.
    { pollingInterval: 5000 },
  )

  const [cancelJob, { isLoading: isCancelling, originalArgs: cancellingId }] =
    useCancelJobMutation()

  const hasActive = useMemo(
    () => (jobs ?? []).some(j => ACTIVE_JOB_STATUSES.includes(j.status)),
    [jobs],
  )

  const handleCancel = async (id: string) => {
    try {
      await cancelJob(id).unwrap()
      toast.success("Job cancelled")
      refetch()
    } catch {
      toast.error("Couldn't cancel that job")
    }
  }

  const openJob = (id: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set("job", id)
        return next
      },
      { replace: false },
    )
  }

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Jobs</h1>
          <p className="text-sm text-system-gray-400">
            Background operations — ad blasts and more. Click a row for details.
            {hasActive && " Live-updating while jobs run."}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-system-blue-800/40 p-2">
        {isLoading && (
          <p className="p-6 text-sm text-system-gray-400">Loading jobs…</p>
        )}
        {isError && (
          <p className="p-6 text-sm text-rose-300">Couldn&apos;t load jobs.</p>
        )}
        {jobs && jobs.length === 0 && (
          <p className="p-6 text-sm text-system-gray-400">
            No jobs yet. Fire a blast from AdBlaster and it&apos;ll show up
            here.
          </p>
        )}
        {jobs && jobs.length > 0 && (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 text-[11px] uppercase tracking-wide text-system-gray-500">
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Job / source</th>
                <th className="py-2 pr-3 font-medium">Result</th>
                <th className="py-2 pr-3 text-right font-medium">When</th>
                <th className="py-2 pr-3 text-right font-medium">Date</th>
                <th
                  className="py-2 text-right font-medium"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <JobRow
                  key={job.id}
                  job={job}
                  onOpen={openJob}
                  onCancel={handleCancel}
                  isCancelling={isCancelling && cancellingId === job.id}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
